import {
    useRef,
    useState
} from 'react';

import {I18n} from '@aws-amplify/core';
import {
    Alert,
    SimpleGrid
} from '@mantine/core';
import {useVerifiedPermissions} from '@flexinet/auth-components';
import {
    MantineDataTable,
    TableSearchForm,
    DataTableTokenPagination,
} from '@flexinet/ui-components';

import {useBillableUnits} from '../hooks/useBillableUnits';
import {useBillableUnitsSearch} from '../hooks/useBillableUnitsSearch';
import {useBillableUnitsSelection} from '../hooks/useBillableUnitsSelection';
import {useBillableUnitsTableColumns} from '../hooks/useBillableUnitsTableColumns';
import {EditFormDrawer} from '../drawers/EditFormDrawer';
import {BillableUnit} from '../types';
import {rbacActions} from '../../../config/rbac/actions';

export const RecordsTable = () => {
    const {selectedRecords, setSelectedRecords} = useBillableUnitsSelection();
    const [selectedRecord, setSelectedRecord] = useState<BillableUnit | null>(null);
    const {allowedActions} = useVerifiedPermissions();

    const {
        items,
        visibleItems,
        sortStatus,
        setSortStatus,
        loading,
        isError,
        nextToken,
        isFetchingMore,
        fetchRecords,
    } = useBillableUnits();

    // Same module-scoped store the list hook filters on (FNP-815).
    const {keyword, handleFilterByKeyword} = useBillableUnitsSearch();

    const scrollViewportRef = useRef<HTMLDivElement>(null);

    // Editing is offered only to an operator who may update (§10).
    const canUpdate = allowedActions.includes(rbacActions.UpdateBillableUnit);
    const {columns} = useBillableUnitsTableColumns({
        onRecordClick: canUpdate ? setSelectedRecord : undefined,
    });

    const filtering = keyword.trim() !== '';

    return (
        <SimpleGrid verticalSpacing='xl' m='sm'>
            <TableSearchForm handleFilterByKeyword={handleFilterByKeyword}/>

            {isError && (
                <Alert color='red' title={I18n.get('Could not load billable units')}>
                    {I18n.get('The billing API did not return the billable units. Try Reload; if it persists, the API may be unavailable in this environment.')}
                </Alert>
            )}

            {/* Two empty states, not one (§3): an empty catalog and a search that matched
                nothing mean opposite things. Gated on !loading so it does not flash during
                the first fetch, and on !isError so a failed load keeps its own Alert. */}
            {!loading && !isError && visibleItems.length === 0 && (
                <Alert
                    color={filtering ? 'gray' : 'blue'}
                    title={filtering ? I18n.get('No matching billable units') : I18n.get('No billable units yet')}
                >
                    {filtering
                        ? `${I18n.get('Nothing in the loaded billable units matches')} "${keyword.trim()}". ${I18n.get('Clear the search to see every loaded row; a match may also be on a page that has not been loaded yet.')}`
                        : I18n.get('No billable unit has been defined yet. Add one to price a meter.')}
                </Alert>
            )}

            <MantineDataTable<BillableUnit>
                columns={columns}
                records={visibleItems}
                // Client-side sort over the loaded pages; see useBillableUnits.
                sortStatus={sortStatus}
                onSortStatusChange={setSortStatus}
                selectedRecords={selectedRecords}
                onSelectedRecordsChange={setSelectedRecords}
                fetching={loading}
                onScrollToBottom={() => fetchRecords()}
                scrollViewportRef={scrollViewportRef}
                withTableBorder={true}
            />

            <DataTableTokenPagination
                loadedCount={visibleItems.length}
                unfilteredCount={items.length}
                filterTerm={keyword}
                noun={'billable unit'}
                nextToken={nextToken}
                isFetchingMore={isFetchingMore}
                handleLoadMore={fetchRecords}
            />

            {selectedRecord && (
                <EditFormDrawer
                    record={selectedRecord}
                    opened={true}
                    onClose={() => setSelectedRecord(null)}
                />
            )}
        </SimpleGrid>
    );
};
